/**
 * LearnPage — the Home tab.
 *
 * iOS parity (home-levels track): sky background, hero with top bar + weekly
 * streak strip + listening-time ring + Continue CTA, the purple CURRENT COURSE
 * hero, the orange streak card, and the level-up celebration overlay. The old
 * "X's Insights" carousel (with the drops/trees cards) is removed; the course
 * path is still rendered by the courses-owned <CourseView />.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import HomeScreenSkeleton from "../components/Home/HomeScreenSkeleton";
import CourseView from "../components/Course/CourseView";
import {
  getUserInfo,
  callWithAuth,
  getUserStats,
  getAllChapters,
} from "../api/Api";
import { GetAllChaptersRequest } from "@shared_types";
import { useCurrentCourse } from "@/hooks/useSelectedCourse";
import {
  FLIGHT_SCHOOL_COURSE_ID,
  getCurrentModuleId,
  useFlightSchoolCourse,
} from "@/features/flight-school";
import { useStreakDays } from "@/hooks/useStreakDays";
import { haptic } from "@/lib/haptic";

import SkyBackground from "../components/Home/SkyBackground";
import ListeningTimeRing from "../components/Home/ListeningTimeRing";
import CurrentCourseHero from "../components/Home/CurrentCourseHero";
import WeeklyStreakStrip from "../components/Streak/WeeklyStreakStrip";
import LevelUpOverlay from "../components/LevelUp/LevelUpOverlay";
import { useLevelUp } from "../components/LevelUp/useLevelUp";

const LearnPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentCourseID, isCurrentCourseLoading } = useCurrentCourse();

  // #2 (iOS HomeScreen.scrollToPathTop): tapping the CURRENT COURSE card scrolls
  // the white course sheet up to just under the transparent top bar — the white
  // panel nearly fills the screen (nav bar + a sliver of sky still visible) —
  // instead of routing to the Courses tab. The page scrolls at the window level.
  const pathSheetRef = React.useRef<HTMLDivElement>(null);
  const scrollToPath = React.useCallback(() => {
    const sheet = pathSheetRef.current;
    if (!sheet) return;
    haptic.medium();
    const top = sheet.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: Math.max(0, top - 64), behavior: "smooth" });
  }, []);

  const userInfoQuery = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });

  const userStatsQuery = useQuery({
    queryKey: ["userStats"],
    queryFn: () => callWithAuth(getUserStats),
  });

  // Shares the cache key with CourseView so we don't double-fetch — which also
  // means this query MUST apply the same Flight School guard. FS is course -1
  // (no backend record); fetching getAllChapters(-1) 500s, and because the key
  // is shared, that failing/loading instance makes CourseView's loading gate
  // stick → the FS Home path never renders (stuck spinner). Skip it for FS.
  const courseInfoQuery = useQuery({
    queryKey: ["courseInfo", currentCourseID],
    queryFn: () => {
      const data: GetAllChaptersRequest = { courseId: currentCourseID || 1 };
      return callWithAuth(getAllChapters, data);
    },
    enabled:
      currentCourseID != null && currentCourseID !== FLIGHT_SCHOOL_COURSE_ID,
  });

  const firstName = userInfoQuery.data?.firstName || "";
  const listeningTime = userStatsQuery.data?.listeningTime || 0;
  // Floor to 1 (iOS useHomeScreenData.ts:247 / AppTopBar.tsx:43, and the web
  // Header) so today counts as the first streak day in the strip.
  const dayStreak = userStatsQuery.data?.dayStreak || 1;

  // Real per-day logged listening (iOS useStreakDays → weeklyListeningData), so
  // the week strip marks days with activity OUTSIDE the consecutive-streak
  // window as completed (iOS computeWeekDays parity). Empty until userId loads,
  // which falls back to the streak-derived view (identical for the common case).
  const weeklyListeningData = useStreakDays(userInfoQuery.data?.id);

  const isFlightSchool = currentCourseID === FLIGHT_SCHOOL_COURSE_ID;
  const courseTitle = isFlightSchool
    ? "Parrot Flight School"
    : courseInfoQuery.data?.course?.title || "Select a course";

  // "Continue" should RESUME where you left off, not dump you in the course
  // catalog. iOS: currentChapter (in_progress/current) ?? apiChapters[0], else
  // push /course/{currentCourseID} — never the catalog (useHomeScreenData.ts:287-316).
  const { completedModules: fsCompletedModules } = useFlightSchoolCourse();
  const chapters = courseInfoQuery.data?.chapters ?? [];
  const currentChapter =
    chapters.find(
      (c) => c.status === "current" || c.status === "in_progress",
    ) ?? chapters[0];
  const continueTarget = isFlightSchool
    ? `/flight-school?moduleId=${getCurrentModuleId(fsCompletedModules)}`
    : currentChapter
      ? `/chapter/${currentChapter.chapterId}`
      : `/course/${currentCourseID}`;

  const { pendingLevel, dismiss } = useLevelUp();

  // Surface loading/error instead of rendering fabricated zeroed progress on a
  // transient auth/network failure of the core home queries.
  // Also show the skeleton while a NEWLY-selected course's chapters load. The
  // Courses tab switches the current course optimistically and navigates here
  // immediately (useSelectedCourse), so this query is `isLoading` (no cached
  // data for the new courseId) until the new path is ready — the skeleton fills
  // that gap instead of flashing the previous course. FS has no courseInfo
  // query (disabled), so it's excluded.
  if (
    userInfoQuery.isLoading ||
    userStatsQuery.isLoading ||
    // Wait on the current-course query too, else currentCourseID is briefly
    // undefined and the hero flashes "Browse courses" / routes Continue to the
    // catalog even when the user has a current course on the server.
    isCurrentCourseLoading ||
    (!isFlightSchool && courseInfoQuery.isLoading)
  ) {
    return <HomeScreenSkeleton />;
  }
  if (userInfoQuery.isError || userStatsQuery.isError) {
    // Offline state over the sky, matching iOS HomeScreenSkeleton OfflineOverlay
    // (HomeScreenSkeleton.tsx:269-298): "📡 You're offline / Check your
    // connection and try again / 🔄 Tap to retry", white text on the sky.
    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        <SkyBackground />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            height: "100%",
            padding: 24,
            textAlign: "center",
            color: "#fff",
          }}
        >
          <span aria-hidden style={{ fontSize: 48 }}>
            📡
          </span>
          <span
            style={{ fontSize: "var(--base-font-size-large)", fontWeight: 700 }}
          >
            You're offline
          </span>
          <span
            style={{ fontSize: "var(--base-font-size-small)", opacity: 0.9 }}
          >
            Check your connection and try again
          </span>
          <button
            type="button"
            onClick={() => {
              userInfoQuery.refetch();
              userStatsQuery.refetch();
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 24px",
              borderRadius: 999,
              background: "#ffffff",
              color: "#0966BF",
              border: "none",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            <span aria-hidden>🔄</span> Tap to retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* iOS-exact home sky (HomeScreen.tsx:240-242): dark-blue → blue → white
          gradient, 140px spinning sun, scattered clouds, NO grass (grass is a
          Profile-only decoration on iOS). */}
      <SkyBackground />

      {/* NO overflow here: the page actually scrolls at the WINDOW level (this
          div's height is never constrained by its ancestors, so overflow:auto
          never engaged). An overflow value would also make this div the sticky
          hero's scrollport — a scroller that never moves — breaking the pin. */}
      <div
        style={{
          position: "relative",
          // Above the transparent top bar (z-5) so the white course sheet
          // scrolls OVER it, but below the bottom nav (z-10). pointer-events
          // none lets taps fall through the transparent top region to the
          // streak pill in the bar; the hero + sheet re-enable them.
          zIndex: 8,
          width: "100%",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: 600,
            margin: "0 auto",
            // Top pad clears the fixed transparent shell header (AppTopBar
            // streak pill / logo / words badge overlay the sky).
            padding: "64px 0 0",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Hero (ON the sky): week strip + listening ring + Continue. These
              sit directly over the SkyBackground; only the course/lesson list
              below gets the white sheet (iOS keeps the same split).
              Sticky at the header's bottom edge so the hero stays PINNED on
              the sky while the white sheet (zIndex 1 below) scrolls up and
              slides over it — iOS pins the sky section the same way. */}
          <div
            style={{
              position: "sticky",
              top: 64,
              zIndex: 0,
              pointerEvents: "auto", // re-enable (parent wrapper is none)
              // Hero fills ~67% of the viewport down to the path drawer's
              // resting position (iOS DRAWER_TOP = SCREEN_HEIGHT*0.67, minus the
              // 64px top bar) so the white sheet rests at the same spot.
              minHeight: "calc(67vh - 64px)",
              display: "flex",
              flexDirection: "column",
              padding: "0 16px",
            }}
          >
            <WeeklyStreakStrip
              dayStreak={dayStreak}
              weeklyData={weeklyListeningData}
            />

            {/* Ring + Continue space-evenly fill the rest of the hero (iOS
              WeeklyCalendar ringAndCta: justifyContent space-evenly). */}
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-evenly",
                alignItems: "center",
              }}
            >
              {/* Listening ring — purely decorative (iOS WeeklyCalendar ring is a
              plain View; the levels sheet is opened only from the Profile tab). */}
              <ListeningTimeRing seconds={listeningTime} />

              {/* Continue CTA — resumes the in-progress chapter / next FS module. */}
              <button
                type="button"
                onClick={() => {
                  haptic.medium();
                  navigate(continueTarget);
                }}
                style={{
                  alignSelf: "center",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "14px 32px",
                  borderRadius: 999,
                  background: "#ffffff",
                  color: "var(--color-blue)",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "var(--base-font-size-large)",
                  fontWeight: 700,
                  boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                }}
              >
                <span aria-hidden>▶</span> Continue
              </button>
            </div>
          </div>

          {/* White rounded sheet rising over the sky — holds the course +
              lesson list (iOS HomeScreen.pathSection: bg colors.background,
              24px top radius, upward shadow). The hero above stays on the sky. */}
          <div
            ref={pathSheetRef}
            style={{
              position: "relative",
              zIndex: 1, // slides OVER the sticky hero pinned behind it
              pointerEvents: "auto", // re-enable (parent wrapper is none)
              marginTop: -24, // iOS pathSection marginTop:-24 (slight overlap)
              // Full-bleed to the padded column's edges so no sky slivers peek
              // past the sheet as it rises over the transparent top bar — cancels
              // the 16px horizontal .appPadding inset (the sky is full-bleed).
              width: "calc(100% + 32px)",
              marginLeft: -16,
              background: "var(--color-bg)",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              boxShadow: "0 -8px 16px rgba(0,0,0,0.12)",
              padding: "20px 16px 24px",
              display: "flex",
              flexDirection: "column",
              gap: 20,
              minHeight: "38vh",
            }}
          >
            {/* Purple CURRENT COURSE hero */}
            <CurrentCourseHero
              courseTitle={courseTitle}
              onClick={scrollToPath}
            />

            {/* Course path (courses-track owned) */}
            <CourseView />
          </div>
        </div>
      </div>

      {/* Level-up celebration overlay */}
      {pendingLevel != null && (
        <LevelUpOverlay
          firstName={firstName}
          level={pendingLevel}
          onContinue={dismiss}
        />
      )}
    </div>
  );
};

export default LearnPage;
